import {Icon} from '@iconify/react';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import Button from '../../../incase-domain/components/button/Button';
import DropdownMenuCompact from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {ConnectionRequestStatus} from '../../../incase-domain/connection';
import {
    INotification,
    ISendPresentationsNotification,
    ISharePresentationsNotification,
    NotificationStatus,
    NotificationType,
} from '../../../incase-domain/notification';
import {PresentationsRequestStatus} from '../../../incase-domain/presentation';
import './notificationItem.scss';
import {IPaginatedSearch} from '../../../incase-domain/search';
import useNotification from '../../../service/hooks/useNotification';
import {prettyTimeAgo} from '../../../utils/dateTimeUtils';
import ConnectionNotificationText from './ConnectionNotificationText';
import ExpiredPresentationPasswordText from './ExpiredPasswordText';
import InvalidPasswordNotificationText from './InvalidPasswordNotificationText';
import NotificationModal from './NotificationModal';
import PresentationNotificationText from './PresentationNotificationText';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';

interface NotificationItemProps {
    notification: INotification;
    paginationInfo: IPaginatedSearch;
    onDeletePageChangeHandler: (deletedCount?: number) => void;
}

function NotificationItem({notification, paginationInfo, onDeletePageChangeHandler}: NotificationItemProps): JSX.Element {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const {
        mutateConnectionRequest,
        mutateSendPresentationsRequest,
        mutateSharePresentationsRequest,
        mutateNotification,
        mutateNotificationAsSeen,
    } = useNotification(paginationInfo, []);

    const handleUpdateConnectionRequest = (connectionRequestId: string, status: ConnectionRequestStatus): void => {
        mutateConnectionRequest.mutate({connectionRequestId, status});
    };

    const handleUpdateSendPresentationsRequest = (
        notification: ISendPresentationsNotification,
        status: PresentationsRequestStatus
    ): void => {
        mutateSendPresentationsRequest.mutate({notification, status});
    };

    const handleUpdateSharePresentationsRequest = (
        notification: ISharePresentationsNotification,
        status: PresentationsRequestStatus
    ): void => {
        mutateSharePresentationsRequest.mutate({notification, status});
    };

    const handleUpdateNotification = (notification: INotification): void => {
        mutateNotification.mutate({notification, onDeletePageChangeHandler});
    };

    const handleOpenModal = (): void => {
        setModalOpen(true);

        if (notification.status === NotificationStatus.ACTIVE) {
            mutateNotificationAsSeen.mutate({notificationIds: [notification._id]});
        }
    };

    const handleCloseModal = (): void => {
        setModalOpen(false);
    };

    const handleDelete = (): void => {
        handleUpdateNotification({
            ...notification,
            status: NotificationStatus.DELETED,
        });
    };

    function getIcon(): string {
        switch (notification.type) {
            case NotificationType.CONNECTION:
                switch (notification.connectionRequest.status) {
                    case ConnectionRequestStatus.ACCEPTED:
                        return 'solar:users-group-rounded-bold';
                    case ConnectionRequestStatus.DECLINED:
                        return 'solar:users-group-rounded-bold';
                    case ConnectionRequestStatus.PENDING:
                        return 'solar:users-group-rounded-bold';
                    default:
                        return 'solar:users-group-rounded-bold';
                }
            case NotificationType.SEND_PRESENTATION:
                return 'solar:presentation-graph-bold';
            case NotificationType.SHARE_PRESENTATION:
                return 'solar:share-bold';
            case NotificationType.INVALID_PASSWORD:
                return 'solar:danger-triangle-bold';
            case NotificationType.PRES_PASS_EXPIRED:
                return 'solar:danger-triangle-bold';
            default:
                return '';
        }
    }

    function getColor(): 'success' | 'error' | 'info' | 'warning' | 'primary' {
        switch (notification.type) {
            case NotificationType.CONNECTION:
                switch (notification.connectionRequest.status) {
                    case ConnectionRequestStatus.ACCEPTED:
                        return 'success';
                    case ConnectionRequestStatus.DECLINED:
                        return 'error';
                    case ConnectionRequestStatus.PENDING:
                        return 'info';
                    default:
                        return 'primary';
                }
            case NotificationType.SEND_PRESENTATION:
                return 'info';
            case NotificationType.SHARE_PRESENTATION:
                return 'info';
            case NotificationType.INVALID_PASSWORD:
                return 'warning';
            case NotificationType.PRES_PASS_EXPIRED:
                return 'warning';
            default:
                return 'primary';
        }
    }

    function getTitle(): string {
        switch (notification.type) {
            case NotificationType.CONNECTION:
                switch (notification.connectionRequest.status) {
                    case ConnectionRequestStatus.ACCEPTED:
                        return t('notifications.connectionRequest.title');
                    case ConnectionRequestStatus.CANCELED:
                        return t('notifications.connectionRequest.title');
                    case ConnectionRequestStatus.DECLINED:
                        return t('notifications.connectionRequest.title');
                    case ConnectionRequestStatus.PENDING:
                        return t('notifications.connectionRequest.title');
                    default:
                        return '';
                }
            case NotificationType.SEND_PRESENTATION:
                return t('notifications.sendPresentation.sendPresentation');
            case NotificationType.SHARE_PRESENTATION:
                return t('notifications.sharePresentation.title');
            case NotificationType.INVALID_PASSWORD:
                return t('notifications.invalidPassword.title');
            case NotificationType.PRES_PASS_EXPIRED:
                return t('notifications.presentationPassword.title');
            default:
                return '';
        }
    }

    function getMessage(): JSX.Element {
        switch (notification.type) {
            case NotificationType.CONNECTION:
                return <ConnectionNotificationText connectionRequest={notification.connectionRequest} />;
            case NotificationType.SEND_PRESENTATION:
                return <PresentationNotificationText notification={notification} />;
            case NotificationType.SHARE_PRESENTATION:
                return <PresentationNotificationText notification={notification} isShare />;
            case NotificationType.INVALID_PASSWORD:
                return <InvalidPasswordNotificationText notification={notification} />;
            case NotificationType.PRES_PASS_EXPIRED:
                return <ExpiredPresentationPasswordText notification={notification} />;
            default:
                return <></>;
        }
    }

    function getActionButtons(): JSX.Element {
        if (notification.status === NotificationStatus.RESOLVED) return <div />;
        if (notification.status === NotificationStatus.DELETED) return <div />;

        if (
            notification.type === NotificationType.CONNECTION &&
            notification.connectionRequest.status === ConnectionRequestStatus.PENDING
        ) {
            const vars = mutateConnectionRequest.variables;
            const isLoadingCurrent = vars?.connectionRequestId === notification.connectionRequest._id && mutateConnectionRequest.isLoading;
            const isLoadingCurrentAccepted = isLoadingCurrent && vars?.status === ConnectionRequestStatus.ACCEPTED;
            const isLoadingCurrentDeclined = isLoadingCurrent && vars?.status === ConnectionRequestStatus.DECLINED;
            return (
                <div className="u-flex u-gap-1">
                    <Button
                        data-cy="btnAcceptRequest"
                        variant="solid"
                        size="sm"
                        color="success"
                        loading={isLoadingCurrentAccepted}
                        leading="ph:check-bold"
                        onClick={() => handleUpdateConnectionRequest(notification.connectionRequest._id, ConnectionRequestStatus.ACCEPTED)}
                    >
                        {t('notifications.connectionRequest.accept')}
                    </Button>
                    <Button
                        data-cy="btnDeclineRequest"
                        variant="solid"
                        size="sm"
                        color="error"
                        loading={isLoadingCurrentDeclined}
                        leading="ph:x-bold"
                        onClick={() => handleUpdateConnectionRequest(notification.connectionRequest._id, ConnectionRequestStatus.DECLINED)}
                    >
                        {t('notifications.connectionRequest.decline')}
                    </Button>
                </div>
            );
        }
        if (notification.type === NotificationType.SEND_PRESENTATION) {
            const vars = mutateSendPresentationsRequest.variables;
            const isLoadingCurrent = vars?.notification._id === notification._id && mutateSendPresentationsRequest.isLoading;

            const isLoadingCurrentAccepted = isLoadingCurrent && vars?.status === PresentationsRequestStatus.ACCEPTED;
            const isLoadingCurrentDeclined = isLoadingCurrent && vars?.status === PresentationsRequestStatus.DECLINED;

            return (
                <div className="u-flex u-gap-1">
                    <Button
                        variant="solid"
                        size="sm"
                        color="success"
                        loading={isLoadingCurrentAccepted}
                        leading="ph:check-bold"
                        onClick={() => handleUpdateSendPresentationsRequest(notification, PresentationsRequestStatus.ACCEPTED)}
                    >
                        {t('notifications.connectionRequest.accept')}
                    </Button>
                    <Button
                        variant="solid"
                        size="sm"
                        color="error"
                        loading={isLoadingCurrentDeclined}
                        leading="ph:x-bold"
                        onClick={() => handleUpdateSendPresentationsRequest(notification, PresentationsRequestStatus.DECLINED)}
                    >
                        {t('notifications.connectionRequest.decline')}
                    </Button>
                </div>
            );
        }
        if (notification.type === NotificationType.SHARE_PRESENTATION) {
            const vars = mutateSharePresentationsRequest.variables;
            const isLoadingCurrent = vars?.notification._id === notification._id && mutateSendPresentationsRequest.isLoading;

            const isLoadingCurrentAccepted = isLoadingCurrent && vars?.status === PresentationsRequestStatus.ACCEPTED;
            const isLoadingCurrentDeclined = isLoadingCurrent && vars?.status === PresentationsRequestStatus.DECLINED;

            return (
                <div className="u-flex u-gap-1">
                    <Button
                        variant="solid"
                        size="sm"
                        color="success"
                        loading={isLoadingCurrentAccepted}
                        leading="ph:check-bold"
                        onClick={() => handleUpdateSharePresentationsRequest(notification, PresentationsRequestStatus.ACCEPTED)}
                    >
                        {t('notifications.connectionRequest.accept')}
                    </Button>
                    <Button
                        variant="solid"
                        size="sm"
                        color="error"
                        loading={isLoadingCurrentDeclined}
                        leading="ph:x-bold"
                        onClick={() => handleUpdateSharePresentationsRequest(notification, PresentationsRequestStatus.DECLINED)}
                    >
                        {t('notifications.connectionRequest.decline')}
                    </Button>
                </div>
            );
        }
        if (notification.type === NotificationType.INVALID_PASSWORD) {
            const vars = mutateNotification.variables;
            const isLoadingCurrent = vars?.notification._id === notification._id && mutateNotification.isLoading;
            const isLoadingCurrentRead = isLoadingCurrent && vars?.notification.status === NotificationStatus.DELETED;

            return (
                <Button
                    size="sm"
                    color="info"
                    loading={isLoadingCurrentRead}
                    leading="solar:pen-2-linear"
                    onClick={() => {
                        handleUpdateNotification({
                            ...notification,
                            status: NotificationStatus.RESOLVED,
                        });

                        navigate(`/settings`);
                    }}
                >
                    {t('common.update')}
                </Button>
            );
        }
        if (notification.type === NotificationType.PRES_PASS_EXPIRED) {
            const vars = mutateNotification.variables;
            const isLoadingCurrent = vars?.notification._id === notification._id && mutateNotification.isLoading;
            const isLoadingCurrentDeleted = isLoadingCurrent && vars?.notification.status === NotificationStatus.DELETED;

            return (
                <Button
                    variant="solid"
                    size="sm"
                    color="info"
                    loading={isLoadingCurrentDeleted}
                    leading="solar:pen-2-linear"
                    onClick={() => {
                        handleUpdateNotification({
                            ...notification,
                            status: NotificationStatus.RESOLVED,
                        });
                        navigate(`/presentations/${notification.presentationId}`);
                    }}
                >
                    {t('common.update')}
                </Button>
            );
        }
        return <></>;
    }

    return (
        <>
            <Button className="c-notificationItem" variant="ghost" square fullWidth onClick={handleOpenModal}>
                <div className={`c-notificationItem__iconWrapper c-notificationItem__iconWrapper--${getColor()} u-mr-1`}>
                    <Icon
                        className={`c-notificationItem__iconWrapper_icon c-notificationItem__iconWrapper_icon--${getColor()}`}
                        icon={getIcon()}
                    />
                    {notification.status === NotificationStatus.ACTIVE && (
                        <div className="c-notificationItem__iconWrapper_activeIndicator" />
                    )}
                </div>
                <div className="c-notificationItem__body">
                    <div className="u-flex u-gap-2 u-v-center">
                        <p className="c-notificationItem__title">{getTitle()}</p>
                        &bull;
                        <div className="c-notificationItem__time">{prettyTimeAgo(notification.created, i18n.language)}</div>
                    </div>
                    <p className="c-notificationItem__message">{getMessage()}</p>
                </div>
                <div style={{flex: '1'}} />
                <div className="c-notificationItem__actionButtons" onClick={(e) => e.stopPropagation()}>
                    {getActionButtons()}
                </div>
                <div className="u-ml-1" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuCompact
                        fixed="left"
                        items={[
                            [
                                {
                                    label: t('common.delete'),
                                    icon: 'solar:trash-bin-minimalistic-linear',
                                    onClick: () => setConfirmOpen(true),
                                },
                            ],
                        ]}
                    >
                        <Button leading="ph:dots-three-vertical-bold" variant="ghost" square size="sm" type="button" />
                    </DropdownMenuCompact>
                </div>
            </Button>

            <NotificationModal
                notification={notification}
                title={getTitle}
                message={getMessage}
                actionButtons={getActionButtons}
                isOpen={modalOpen}
                onClose={handleCloseModal}
            />

            <ConfirmModal
                isOpen={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={() => {
                    setConfirmOpen(false);
                    handleDelete();
                }}
                icon="solar:trash-bin-minimalistic-linear"
                color="error"
                confirmText={t('common.delete')}
            />
        </>
    );
}

export default NotificationItem;
